import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import { Marketplace } from '../../types';
import { deleteMarketplace, getMarketplaces } from '../../services/api';
import MarketplaceDialog from './MarketplaceDialog';

const MarketplaceManagementPanel: React.FC = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [editingMarketplace, setEditingMarketplace] = useState<Marketplace | null>(null);

    const { data: marketplaces = [], isLoading, error } = useQuery({
        queryKey: ['marketplaces'],
        queryFn: getMarketplaces,
    });

    const deleteMutation = useMutation({
        mutationFn: deleteMarketplace,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketplaces'] }),
    });

    const handleOpenDialog = (marketplace: Marketplace | null = null) => {
        setEditingMarketplace(marketplace);
        setDialogOpen(true);
    };

    const columns: GridColDef<Marketplace>[] = [
        {
            field: 'name',
            headerName: t('marketplace.columns.name'),
            flex: 1
        },
        {
            field: 'code',
            headerName: t('marketplace.columns.code'),
            width: 150
        },
        {
            field: 'actions',
            headerName: t('marketplace.columns.actions'),
            type: 'actions',
            width: 100,
            renderCell: (params: GridRenderCellParams<Marketplace>) => (
                <>
                    <IconButton
                        onClick={() => handleOpenDialog(params.row)}
                        aria-label={t('marketplace.actions.edit')}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        onClick={() => deleteMutation.mutate(params.row.id)}
                        aria-label={t('marketplace.actions.delete')}
                    >
                        <DeleteIcon />
                    </IconButton>
                </>
            ),
        },
    ];

    return (
        <Paper sx={{ p: 2, height: '70vh', mt: 4, display: 'flex', flexDirection: 'column' }}>
            <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">
                        {t('marketplace.management_title')}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        {t('marketplace.add_marketplace')}
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error">
                        {(error as Error).message}
                    </Alert>
                )}

                <Box sx={{ flexGrow: 1 }}>
                    <DataGrid
                        rows={marketplaces}
                        columns={columns}
                        loading={isLoading}
                        sx={{ border: 0 }}
                        getRowId={(row) => row.id}
                        localeText={{
                            // MUI DataGrid локализация
                            noRowsLabel: t('marketplace.no_rows'),
                            noResultsOverlayLabel: t('marketplace.no_results'),
                            errorOverlayDefaultLabel: t('marketplace.error_overlay'),
                            toolbarDensity: t('marketplace.toolbar_density'),
                            toolbarDensityLabel: t('marketplace.toolbar_density_label'),
                            toolbarDensityCompact: t('marketplace.toolbar_density_compact'),
                            toolbarDensityStandard: t('marketplace.toolbar_density_standard'),
                            toolbarDensityComfortable: t('marketplace.toolbar_density_comfortable')
                        }}
                    />
                </Box>

                <MarketplaceDialog
                    open={isDialogOpen}
                    onClose={() => setDialogOpen(false)}
                    marketplace={editingMarketplace}
                />
            </>
        </Paper>
    );
};

export default MarketplaceManagementPanel;
